import { queryOptions } from '@tanstack/react-query';
import { api } from '../api-client';
import type {
  CreatedApiKey,
  EnvironmentType,
  InstallSnippet,
  ListResponse,
  Ok,
  Site,
  SiteApiKey,
  SiteDetail,
  SiteDomain,
  SiteEnvironment,
  SiteWithEnvironments,
} from './types';

export const siteKeys = {
  all: (orgId: string) => ['sites', orgId] as const,
  list: (orgId: string) => [...siteKeys.all(orgId), 'list'] as const,
  detail: (orgId: string, siteId: string) => [...siteKeys.all(orgId), 'detail', siteId] as const,
  apiKeys: (orgId: string, siteId: string) => [...siteKeys.all(orgId), siteId, 'api-keys'] as const,
  install: (orgId: string, siteId: string, environmentId: string) =>
    [...siteKeys.all(orgId), siteId, 'install', environmentId] as const,
};

export interface CreateSiteInput {
  name: string;
  slug?: string;
  description?: string;
  defaultLocale?: string;
  createStagingEnvironment?: boolean;
  baseUrl?: string;
}

export interface UpdateSiteInput {
  name?: string;
  description?: string | null;
  defaultLocale?: string;
  locales?: string[];
  status?: 'active' | 'archived';
}

export interface CreateEnvironmentInput {
  type: EnvironmentType;
  name?: string;
  slug?: string;
  baseUrl?: string;
  allowedOrigins?: string[];
}

export interface UpdateEnvironmentInput {
  name?: string;
  baseUrl?: string | null;
  allowedOrigins?: string[];
}

const base = (orgId: string) => `/organisations/${orgId}/sites`;

export const sitesApi = {
  list: (orgId: string) => api.get<ListResponse<SiteWithEnvironments>>(base(orgId)),

  create: (orgId: string, input: CreateSiteInput) =>
    api.post<SiteWithEnvironments>(base(orgId), input),

  detail: (orgId: string, siteId: string) => api.get<SiteDetail>(`${base(orgId)}/${siteId}`),

  update: (orgId: string, siteId: string, input: UpdateSiteInput) =>
    api.patch<Site>(`${base(orgId)}/${siteId}`, input),

  remove: (orgId: string, siteId: string) => api.delete<Ok>(`${base(orgId)}/${siteId}`),

  createEnvironment: (orgId: string, siteId: string, input: CreateEnvironmentInput) =>
    api.post<SiteEnvironment>(`${base(orgId)}/${siteId}/environments`, input),

  updateEnvironment: (
    orgId: string,
    siteId: string,
    environmentId: string,
    input: UpdateEnvironmentInput,
  ) => api.patch<SiteEnvironment>(`${base(orgId)}/${siteId}/environments/${environmentId}`, input),

  rotateKey: (orgId: string, siteId: string, environmentId: string) =>
    api.post<SiteEnvironment>(`${base(orgId)}/${siteId}/environments/${environmentId}/rotate-key`),

  install: (orgId: string, siteId: string, environmentId: string) =>
    api.get<InstallSnippet>(`${base(orgId)}/${siteId}/environments/${environmentId}/install`),

  addDomain: (
    orgId: string,
    siteId: string,
    input: { hostname: string; siteEnvironmentId: string; isPrimary?: boolean },
  ) => api.post<SiteDomain>(`${base(orgId)}/${siteId}/domains`, input),

  removeDomain: (orgId: string, siteId: string, domainId: string) =>
    api.delete<Ok>(`${base(orgId)}/${siteId}/domains/${domainId}`),

  apiKeys: (orgId: string, siteId: string) =>
    api.get<ListResponse<SiteApiKey>>(`${base(orgId)}/${siteId}/api-keys`),

  createApiKey: (
    orgId: string,
    siteId: string,
    input: { name: string; scopes: string[]; expiresInDays?: number },
  ) => api.post<CreatedApiKey>(`${base(orgId)}/${siteId}/api-keys`, input),

  revokeApiKey: (orgId: string, siteId: string, apiKeyId: string) =>
    api.delete<Ok>(`${base(orgId)}/${siteId}/api-keys/${apiKeyId}`),
};

export const sitesQuery = (orgId: string) =>
  queryOptions({
    queryKey: siteKeys.list(orgId),
    queryFn: () => sitesApi.list(orgId),
    select: (data: ListResponse<SiteWithEnvironments>) => data.items,
  });

export const siteDetailQuery = (orgId: string, siteId: string) =>
  queryOptions({
    queryKey: siteKeys.detail(orgId, siteId),
    queryFn: () => sitesApi.detail(orgId, siteId),
  });

export const siteApiKeysQuery = (orgId: string, siteId: string) =>
  queryOptions({
    queryKey: siteKeys.apiKeys(orgId, siteId),
    queryFn: () => sitesApi.apiKeys(orgId, siteId),
    select: (data: ListResponse<SiteApiKey>) => data.items,
  });

export const installSnippetQuery = (orgId: string, siteId: string, environmentId: string) =>
  queryOptions({
    queryKey: siteKeys.install(orgId, siteId, environmentId),
    queryFn: () => sitesApi.install(orgId, siteId, environmentId),
    staleTime: 5 * 60_000,
  });
